<template>
  <!--
    The customer testimonial carousel. Lived inline on the Pricing page until
    Steve asked for the same carousel on Customer Stories and Watch Demo
    (18 Sep 2026), so it is one component now. Cards are data (`items`):
    a quote with a headline, and either a logo or a text attribution.
    Card and title styles are global (custom.scss .testimonial-*).
  -->
  <section class="testimonial-carousel">
    <v-row no-gutters>
      <v-col cols="12" align-self="center">
        <v-container>
          <div class="testimonial-title text-center">
            <img
              src="/static/homepage/testimonial/improve-customer-testimonials.png"
              alt=""
            />
            <h2>{{ title }}</h2>
            <p>{{ lead }}</p>
          </div>
        </v-container>
      </v-col>
    </v-row>
    <v-container>
      <VueSlickCarousel v-bind="settings">
        <div v-for="item in items" :key="item.headline" class="testimonial-item">
          <div class="testimonial-card">
            <div>
              <p v-if="item.eyebrow" class="testimonial-eyebrow">{{ item.eyebrow }}</p>
              <h2>“{{ item.headline }}”</h2>
              <p>“{{ item.quote }}”</p>
            </div>
            <div class="testimonial-foot">
              <img v-if="item.logo" :src="item.logo" :alt="item.logoAlt || item.company" />
              <!-- A named person stays named under their logo (25 Sep 2026):
                   dropping Scott Knowles' name for the Chamber's logo would
                   misattribute his words. -->
              <p v-if="item.attribution" class="testimonial-attrib">
                <strong>{{ item.attribution }}</strong><br />
                {{ item.role }}
              </p>
            </div>
          </div>
        </div>
      </VueSlickCarousel>
    </v-container>
  </section>
</template>

<script>
import VueSlickCarousel from 'vue-slick-carousel';
import 'vue-slick-carousel/dist/vue-slick-carousel.css';
import 'vue-slick-carousel/dist/vue-slick-carousel-theme.css';
import { CAROUSEL_TESTIMONIALS } from '@/data/testimonials';

export default {
  name: 'TestimonialCarousel',
  components: { VueSlickCarousel },
  props: {
    title: { type: String, default: 'Customer Testimonials' },
    lead: { type: String, default: 'What our customers say about IMPROVE' },
    items: { type: Array, default: () => CAROUSEL_TESTIMONIALS },
  },
  data() {
    return {
      settings: {
        infinite: true,
        autoplay: true,
        dots: true,
        speed: 300,
        slidesToShow: 3,
        slidesToScroll: 1,
        initialSlide: 0,
        responsive: [
          { breakpoint: 1200, settings: { slidesToShow: 2, slidesToScroll: 1 } },
          { breakpoint: 959, settings: { slidesToShow: 1, slidesToScroll: 1 } },
        ],
      },
    };
  },
};
</script>

<style scoped>
.testimonial-carousel {
  margin-bottom: 40px;
}

/* Cards used to be a fixed 350px (290px below 1264px), which the longer
   named quotes of 25 Sep 2026 overflow. Every card in the row now stretches
   to the tallest one, so the row stays even whatever the quote length. */
.testimonial-carousel >>> .slick-track {
  display: flex;
}

.testimonial-carousel >>> .slick-slide {
  height: auto;
}

.testimonial-carousel >>> .slick-slide > div,
.testimonial-carousel .testimonial-item {
  height: 100%;
}

.testimonial-carousel .testimonial-card {
  height: 100%;
  min-height: 350px;
}

.testimonial-eyebrow {
  margin: 0 0 8px !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--brand-teal, #08a0a8) !important;
}

.testimonial-foot {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
}

.testimonial-attrib {
  margin: 0;
  font-size: 13px !important;
  line-height: 1.4;
  color: #6b7280 !important;
}

.testimonial-attrib strong {
  color: #002458;
}
</style>
